import React, { useEffect, useState } from 'react';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { Typeahead } from 'react-bootstrap-typeahead';
import moment from 'moment';
import * as _ from 'lodash/fp';

import {
  InsuranceEventType,
  InsuranceProvider,
  Origin,
} from '@upstart/harmony/lib/constants';
import {
  InsuranceEventCounts,
  InsuranceApplicationTotals,
} from '@upstart/harmony/lib/legos/dealerTypes';

import DealerService from 'services/DealerService';
import './InsuranceEventSummary.scss';
import { QuickStats } from './QuickStats';
import { SummaryTable, Row } from './SummaryTable';

interface Filters {
  dealerName: string[];
  providerName: string[];
}

const DEFAULT_START_TIME = moment()
  .subtract(29, 'days')
  .startOf('day');
const DEFAULT_END_TIME = moment().endOf('day');
const DATE_FORMAT = 'YYYY/MM/DD';
const getUniqFields = (fieldName: string) =>
  _.flow(_.map(_.get(fieldName)), _.uniq);

function countsToRow({
  dealerName,
  providerName,
  applications,
  texts,
}: InsuranceEventCounts): Row {
  return {
    dealerName,
    providerName,
    onlineApplications: applications[Origin.ONLINE],
    inStoreApplications: applications[Origin.INSTORE],
    textSent: texts[InsuranceEventType.TEXT_SENT],
    textClick: texts[InsuranceEventType.TEXT_CLICK],
    textConverted: texts[InsuranceEventType.TEXT_CONVERSION],
  };
}

// gets a filter function to apply to application events,
// over a filter of type 'Filters'
const getFilter = (filters: Filters) => (eventCounts: InsuranceEventCounts) =>
  _.every(
    _.identity,
    Object.entries(filters).map(
      ([key, values]) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        !values.length || values.includes((eventCounts as any)[key]),
    ),
  );

function totalsToRows(totals: InsuranceApplicationTotals): [string, number][] {
  return [
    ['Total Online', totals.origins[Origin.ONLINE]],
    ['Total Instore', totals.origins[Origin.INSTORE]],
    ['Total Cover Leads', totals.providers[InsuranceProvider.COVER]],
    ['Total Root Leads', totals.providers[InsuranceProvider.ROOT]],
    ['Total Jerry Leads', totals.providers[InsuranceProvider.JERRY]],
    ['Total Text Enhanced', totals.text],
  ];
}

function useLogic(
  fetchEventsInRange = DealerService.getAggregateInsuranceLeads,
) {
  const [startDate, setStartDate] = useState(DEFAULT_START_TIME);
  const [endDate, setEndDate] = useState(DEFAULT_END_TIME);
  const [filterDealers, setFilterDealers] = useState<string[]>([]);
  const [filterProviders, setFilterProviders] = useState<string[]>([]);
  const [counts, setCounts] = useState<InsuranceEventCounts[]>([]);
  const [eventTotalRows, setEventTotalRows] = useState<[string, number][]>([]);
  useEffect(() => {
    fetchEventsInRange(startDate.toISOString(), endDate.toISOString()).then(
      data => {
        setCounts(data.counts);
        setEventTotalRows(totalsToRows(data.totals));
      },
    );
  }, [startDate, endDate, fetchEventsInRange]);
  const filterByDealerProvider = getFilter({
    dealerName: filterDealers,
    providerName: filterProviders,
  });
  const dealers = getUniqFields('dealerName')(counts).filter(
    (x: any) => x !== undefined,
  );
  const providers = getUniqFields('providerName')(counts).filter(
    (x: any) => x !== undefined,
  );
  return {
    startDate,
    endDate,
    dealers,
    filterDealers,
    setFilterDealers,
    providers,
    filterProviders,
    setFilterProviders,
    dateRangeDescription:
      startDate.valueOf() === DEFAULT_START_TIME.valueOf() &&
      endDate.valueOf() === DEFAULT_END_TIME.valueOf()
        ? 'Last 30 Days'
        : 'Custom',
    applyRange: (
      _event: Function,
      newRange: {
        startDate: moment.Moment;
        endDate: moment.Moment;
      },
    ) => {
      setStartDate(newRange.startDate);
      setEndDate(newRange.endDate);
    },
    eventCountRows: counts.filter(filterByDealerProvider).map(countsToRow),
    eventTotalRows,
  };
}

export function InsuranceEventSummary() {
  const logic = useLogic();
  const dateSection = (
    <>
      <span className="f-medium pt-5 pl-5 b">
        {' '}
        ({logic.dateRangeDescription}){' '}
      </span>
      <DateRangePicker
        startDate={logic.startDate}
        endDate={logic.endDate}
        onApply={logic.applyRange}
      >
        <button
          type="button"
          className="btn btn-primary margin-left margin-right"
        >
          <i className="glyphicon glyphicon-calendar" />
        </button>
        <span className="display-range-label">
          {logic.startDate.format(DATE_FORMAT)} -{' '}
          {logic.endDate.format(DATE_FORMAT)}
        </span>
      </DateRangePicker>
    </>
  );
  const filterSection = (
    <div className="flex w-50 pb-5">
      <Typeahead
        className="w-50"
        id="dealer-filter"
        placeholder="Filter by dealer"
        multiple
        selected={logic.filterDealers}
        onChange={logic.setFilterDealers}
        options={logic.dealers}
      />
      <Typeahead
        id="provider-filter"
        className="w-50 pl-5"
        placeholder="Filter by provider"
        multiple
        selected={logic.filterProviders}
        onChange={logic.setFilterProviders}
        options={logic.providers}
      />
    </div>
  );
  return (
    <div>
      <div className="flex items-center">
        <span className="f-big"> Insurance Results </span>
        {dateSection}
      </div>
      <div className="quick-stats-spacer" />
      <QuickStats totals={logic.eventTotalRows} />
      <div className="table-spacer" />
      {filterSection}
      <SummaryTable rows={logic.eventCountRows} />
    </div>
  );
}

export const test = {
  useLogic,
};
